import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useIsAdmin } from '../auth/hooks';
import { logout } from '../auth/login';
import { useAppUpdate } from '../hooks/useAppUpdate';
import DevOrgSelector from './DevOrgSelector';
import { HybridIcon } from './HybridIcon';
import './Layout.css';

const BUILD_VERSION = __APP_VERSION__;
const BUILD_DATE = new Date(__BUILD_TIME__).toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { needRefresh, applyUpdate, dismiss } = useAppUpdate();
  const {
    isLocalImpersonation,
    organization,
    selectedLocalRole,
    setSelectedOrganizationId,
    user,
  } = useAuth();
  const isAdmin = useIsAdmin();

  const handleLogout = async () => {
    if (isLocalImpersonation) {
      setSelectedOrganizationId(null);
      navigate('/login');
      return;
    }

    await logout();
    navigate('/login');
  };

  const primaryNav = [
    { path: '/overview', label: 'Today', icon: 'speedometer' },
    { path: '/rentals', label: 'Rentals', icon: 'list' },
    { path: '/cars', label: 'Fleet', icon: 'car' },
    { path: '/calendar', label: 'Calendar', icon: 'calendar' },
    { path: '/customers', label: 'Customers', icon: 'people' },
    { path: '/services', label: 'Maintenance', icon: 'construct' },
  ];

  const secondaryNav = [
    { path: '/settings', label: 'Settings', icon: 'settings' },
    { path: '/help', label: 'Help', icon: 'help-circle' },
  ];

  const mobileNav = [
    { path: '/overview', label: 'Today', icon: 'speedometer' },
    { path: '/cars', label: 'Cars', icon: 'car' },
    { path: '/rentals/new', label: 'Book', icon: 'add-circle', isCta: true },
    { path: '/rentals', label: 'Rentals', icon: 'list' },
    { path: '/settings', label: 'More', icon: 'ellipsis-horizontal' },
  ];

  const isRouteActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isMoreActive =
    isRouteActive('/settings') ||
    isRouteActive('/help') ||
    isRouteActive('/customers') ||
    isRouteActive('/services');

  return (
    <div className="layout-shell">
      {needRefresh && (
        <div className="update-banner" role="alert">
          <span>A new version of Path Finder is available.</span>
          <div className="update-banner-actions">
            <button className="update-banner-btn" onClick={applyUpdate}>
              Update Now
            </button>
            <button className="update-banner-dismiss" onClick={dismiss} aria-label="Dismiss update">
              ✕
            </button>
          </div>
        </div>
      )}

      {isLocalImpersonation && (
        <div className="local-dev-banner" role="status" aria-live="polite">
          <div className="local-dev-banner-copy">
            <strong>Developer Mode</strong>
            <span>
              Impersonating {user?.displayName || user?.email || 'Developer'} in {organization?.name || 'selected organization'} with {selectedLocalRole} access.
            </span>
          </div>
        </div>
      )}

      <div className="layout-frame">
        <aside className="desktop-sidebar">
          <div className="desktop-sidebar-top">
            <Link to="/overview" className="brand-lockup">
              <div className="brand-mark">
                <img src="/pathFinderLogo.png" alt="Path Finder Logo" className="brand-mark-image" />
              </div>
              <div>
                <p className="eyebrow">Fleet OS</p>
                <h1 className="brand-title">Path Finder</h1>
              </div>
            </Link>

            <div className="desktop-sidebar-panel card">
              <p className="eyebrow">Workspace</p>
              <div className="sidebar-panel-copy">
                <strong>{organization?.name || 'Current organization'}</strong>
                <span>{user?.displayName || user?.email}</span>
              </div>
              <div className="sidebar-panel-meta">
                {isAdmin && <span className="pill pill-accent">Admin</span>}
                {isLocalImpersonation && <span className="pill pill-warn">Local Dev</span>}
              </div>
            </div>
          </div>

          <nav className="desktop-nav" aria-label="Primary">
            {primaryNav.map((item) => {
              const isActive = isRouteActive(item.path);
              return (
                <Link key={item.path} to={item.path} className={`desktop-nav-link ${isActive ? 'is-active' : ''}`}>
                  <HybridIcon name={item.icon} size={20} color="currentColor" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="desktop-sidebar-bottom">
            <div className="desktop-nav-secondary" aria-label="Secondary">
              {secondaryNav.map((item) => {
                const isActive = isRouteActive(item.path);
                return (
                  <Link key={item.path} to={item.path} className={`desktop-nav-link desktop-nav-link-secondary ${isActive ? 'is-active' : ''}`}>
                    <HybridIcon name={item.icon} size={18} color="currentColor" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>

            <DevOrgSelector variant="panel" />

            <div className="sidebar-actions">
              <button type="button" className="btn btn-secondary btn-full" onClick={handleLogout} aria-label="Logout">
                <HybridIcon name="log-out" size={18} color="currentColor" />
                <span>Log out</span>
              </button>
            </div>

            <div className="sidebar-build">
              <span>v{BUILD_VERSION}</span>
              <span>{BUILD_DATE}</span>
            </div>
          </div>
        </aside>

        <main className="layout-main">
          <div className="mobile-topbar">
            <div className="mobile-brand">
              <div className="brand-mark brand-mark-mobile">
                <img src="/pathFinderLogo.png" alt="Path Finder Logo" className="brand-mark-image" />
              </div>
              <div>
                <p className="eyebrow">Today</p>
                <h2 className="mobile-brand-title">Path Finder</h2>
              </div>
            </div>

            <div className="mobile-topbar-actions">
              {isAdmin && <span className="pill pill-accent">Admin</span>}
              {isLocalImpersonation && <span className="pill pill-warn">Dev</span>}
              <button
                type="button"
                className="btn btn-secondary btn-icon"
                onClick={() => navigate('/help')}
                aria-label="Open Help"
              >
                <HybridIcon name="help-circle" size={18} color="currentColor" />
              </button>
              <button type="button" className="btn btn-secondary btn-icon" onClick={handleLogout} aria-label="Logout">
                <HybridIcon name="log-out" size={18} color="currentColor" />
              </button>
            </div>
          </div>

          <div className="mobile-dev-selector">
            <DevOrgSelector />
          </div>

          <div key={location.pathname} className="layout-screen screen-enter">
            <div className="responsive-container">{children}</div>
          </div>
        </main>
      </div>

      <nav className="mobile-tabbar" aria-label="Primary mobile navigation">
        {mobileNav.map((item) => {
          const isActive = item.label === 'More' ? isMoreActive : isRouteActive(item.path);
          return (
            <Link key={item.label} to={item.path} className={`mobile-tab ${item.isCta ? 'is-cta' : ''} ${isActive ? 'is-active' : ''}`}>
              <span className="mobile-tab-icon">
                <HybridIcon name={item.icon} size={item.isCta ? 24 : 20} color="currentColor" />
              </span>
              <span className="mobile-tab-label">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

